import { kr, gaeldsfaktor, realkreditLtvCap, bidragssats, type PropertyType } from '@heivol-finance/calc';
import { formatOre, formatNumber, formatPercent, currencySymbol } from '../lib/format.js';
import { useLoanCalculator } from '../hooks/useLoanCalculator.js';
import { PageHeader, Card, StatCard, MoneyField } from '../components/ui.js';

const TYPES: { value: PropertyType; label: string }[] = [
  { value: 'ejerbolig', label: 'Owner-occupied (year-round)' },
  { value: 'fritidshus', label: 'Holiday home' },
  { value: 'erhverv', label: 'Commercial' },
];

/** Plain numeric field (rates, counts, term). Value/onChange are plain numbers. */
function Field({
  label,
  value,
  onChange,
  suffix,
  step,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  suffix?: string;
  step?: number;
}): JSX.Element {
  return (
    <label className="block">
      <span className="text-hv-sm text-hv-text-secondary">{label}</span>
      <div className="mt-1 flex items-center rounded-hv-md border border-hv-border bg-hv-surface focus-within:shadow-hv-focus">
        <input
          type="number"
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full bg-transparent px-3 py-2 text-hv-text outline-none"
        />
        {suffix ? <span className="px-3 text-hv-sm text-hv-text-tertiary">{suffix}</span> : null}
      </div>
    </label>
  );
}

function SubHead({ children }: { children: React.ReactNode }): JSX.Element {
  return <h2 className="mb-3 text-hv-lg font-medium text-hv-text">{children}</h2>;
}

export function LoanCalculator(): JSX.Element {
  const {
    grossMonthly, setGrossMonthly,
    existingDebt, setExistingDebt,
    maxFactor, setMaxFactor,
    price, setPrice,
    type, setType,
    ratePct, setRatePct,
    termYears, setTermYears,
    netMonthly, setNetMonthly,
    fixedMonthly, setFixedMonthly,
    adults, setAdults,
    children, setChildren,
    savings, setSavings,
    grossAnnual,
    kobekraft,
    split,
    resultingFactor,
    ltv,
    payment,
    raad,
    costs,
    scenarioA,
    scenarioB,
  } = useLoanCalculator();

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Loan Calculator"
        subtitle={
          <>
            Debt-to-income (gældsfaktor), loan-to-value (belåningsgrad), disposable amount (rådighedsbeløb) and monthly payment under Danish rules.{' '}
            <span className="text-hv-text-tertiary">Guidance — not advice.</span>
          </>
        }
      />

      {/* Capacity inputs */}
      <Card className="mb-6 p-4">
        <SubHead>Purchasing power &amp; financing</SubHead>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <MoneyField label="Gross monthly income (household)" value={grossMonthly} onChange={setGrossMonthly} suffix={`${currencySymbol()}/mo.`} />
          <MoneyField label="Existing debt" value={existingDebt} onChange={setExistingDebt} />
          <Field label="Max debt-to-income (gældsfaktor)" value={maxFactor} onChange={setMaxFactor} step={0.1} />
          <MoneyField label="Property price" value={price} onChange={setPrice} />
          <label className="block">
            <span className="text-hv-sm text-hv-text-secondary">Property type</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as PropertyType)}
              className="mt-1 w-full rounded-hv-md border border-hv-border bg-hv-surface px-3 py-2 text-hv-text focus-visible:shadow-hv-focus"
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StatCard label="Purchasing power (max debt)" value={formatOre(kobekraft)} tone="ai" />
          <StatCard
            label="Debt-to-income at this property"
            value={formatNumber(Math.round(resultingFactor * 100) / 100)}
            tone={resultingFactor > 4 ? 'warning' : 'success'}
            hint={resultingFactor > 4 ? '> 4 is considered high (Finanstilsynet)' : 'Within the guideline'}
          />
          <StatCard label="Mortgage (realkredit) — LTV cap" value={formatOre(split.realkredit)} hint={`${formatPercent(realkreditLtvCap(type))} of the price`} />
          <StatCard label="Bank loan (top-up)" value={formatOre(split.bankloan)} />
          <StatCard label="Down payment (udbetaling, min. 5%)" value={formatOre(split.udbetaling)} />
          <StatCard label="Loan-to-value / LTV (belåningsgrad)" value={formatPercent(ltv)} hint={ltv > 0.6 ? '> 60% = high (Finanstilsynet)' : 'Within the guideline'} />
        </div>
      </Card>

      {/* Payment breakdown */}
      <Card className="mb-6 p-4">
        <SubHead>Monthly payment (30-year fixed, stress test)</SubHead>
        <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Interest" value={ratePct} onChange={setRatePct} suffix="% p.a." step={0.1} />
          <Field label="Term" value={termYears} onChange={setTermYears} suffix="yr" />
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Payment / mo." value={formatOre(payment.monthly)} tone="ai" />
          <StatCard label="Of which interest (mo. 1)" value={formatOre(payment.firstInterest)} tone="warning" hint="Consumptive" />
          <StatCard label="Of which principal (mo. 1)" value={formatOre(payment.firstPrincipal)} hint="Net-worth-neutral" />
          <StatCard label="Contribution fee (bidrag) / mo." value={formatOre(payment.bidragMonthly)} hint={`Contribution rate ${formatPercent(bidragssats(ltv))}`} />
          <StatCard label="Payment incl. contribution fee" value={formatOre(payment.totalWithBidrag)} tone="chat" />
          <StatCard label="Total interest (lifetime)" value={formatOre(payment.lifetimeInterest)} hint={`over ${termYears} years`} />
        </div>
      </Card>

      {/* Rådighedsbeløb */}
      <Card className="mb-6 p-4">
        <SubHead>Disposable amount (rådighedsbeløb)</SubHead>
        <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MoneyField label="Net monthly income" value={netMonthly} onChange={setNetMonthly} />
          <MoneyField label="Fixed expenses (excl. housing)" value={fixedMonthly} onChange={setFixedMonthly} />
          <Field label="Adults" value={adults} onChange={setAdults} />
          <Field label="Children" value={children} onChange={setChildren} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard label="Disposable amount / mo." value={formatOre(raad.disposable)} hint="After housing + fixed expenses" />
          <StatCard label="Guideline minimum" value={formatOre(raad.requiredMin)} hint={`${adults} adults + ${children} children`} />
          <StatCard label="Test" value={raad.passes ? 'Passes ✓' : 'Fails ✗'} tone={raad.passes ? 'success' : 'warning'} />
        </div>
      </Card>

      {/* One-time costs */}
      <Card className="mb-6 p-4">
        <SubHead>One-time costs at purchase</SubHead>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard label="Registration tax (tinglysning, fixed)" value={formatOre(costs.tinglysningFixed)} />
          <StatCard label="Registration tax (1.25% of loan)" value={formatOre(costs.tinglysningVariable)} />
          <StatCard label="Total one-time" value={formatOre(costs.total)} tone="ai" />
        </div>
      </Card>

      {/* Scenario comparison */}
      <Card className="p-4">
        <SubHead>Scenarios</SubHead>
        <div className="mb-4 max-w-xs">
          <MoneyField label="Savings / equity" value={savings} onChange={setSavings} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[scenarioA, scenarioB].map((s) => (
            <div key={s.name} className="rounded-hv-lg border border-hv-border bg-hv-surface-alt p-4">
              <div className="mb-2 text-hv-sm font-medium text-hv-text">{s.name}</div>
              <Row label="Total cost" value={formatOre(s.gross)} />
              <Row label="− Savings" value={`−${formatOre(s.savings)}`} />
              <div className="mt-2 flex items-center justify-between border-t border-hv-border pt-2">
                <span className="text-hv-sm text-hv-text-secondary">To be financed</span>
                <span className="text-hv-lg font-medium tabular-nums text-hv-ai-text">{formatOre(s.needed)}</span>
              </div>
              <div className="mt-1 text-hv-xs text-hv-text-tertiary">
                Debt-to-income (gældsfaktor): {formatNumber(Math.round(gaeldsfaktor(s.needed + kr(existingDebt), grossAnnual) * 100) / 100)}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-hv-xs text-hv-text-tertiary">
          Figures and rates are ⚠ baseline — see <span className="font-mono">danish-personal-finance-rules</span>. Reproduces the spreadsheet's{' '}
          <span className="font-mono">LoanCalculator</span> tab. Reference check: purchasing power at 55,000/mo. &amp; factor 4.0 ={' '}
          {formatOre(kr(2640000))}.
        </p>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between py-0.5 text-hv-sm">
      <span className="text-hv-text-secondary">{label}</span>
      <span className="tabular-nums text-hv-text">{value}</span>
    </div>
  );
}
